import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export const TrafficPanel = () => {
  const { t } = useLang();
  const tr = t.dash.traffic;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats/traffic").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data || data.total_visits === 0) return null;
  const maxViews = Math.max(1, ...data.top_sections.map((s) => s.views));

  return (
    <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-10" data-testid="traffic-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-6">
        <BarChart3 className="w-4 h-4 text-[#FF7900]" /> {tr.title}
      </p>
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="flex gap-8 mb-5">
            <div>
              <p className="font-display text-3xl font-semibold" data-testid="traffic-visits">{data.total_visits}</p>
              <p className="text-xs text-white/40 uppercase tracking-widest mt-1">{tr.visits}</p>
            </div>
            <div>
              <p className="font-display text-3xl font-semibold text-[#FF7900]">{data.leads}</p>
              <p className="text-xs text-white/40 uppercase tracking-widest mt-1">{tr.leads}</p>
            </div>
            <div>
              <p className="font-display text-3xl font-semibold text-emerald-400" data-testid="traffic-conversion">{data.conversion}%</p>
              <p className="text-xs text-white/40 uppercase tracking-widest mt-1">{tr.conversion}</p>
            </div>
            {data.apollo_visits > 0 && (
              <div>
                <p className="font-display text-3xl font-semibold text-sky-400" data-testid="traffic-apollo">{data.apollo_visits}</p>
                <p className="text-xs text-white/40 uppercase tracking-widest mt-1">{tr.apollo}</p>
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={data.series}>
              <defs>
                <linearGradient id="trafficFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF7900" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#FF7900" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
              <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 }} />
              <Area type="monotone" dataKey="visits" stroke="#FF7900" strokeWidth={2} fill="url(#trafficFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div data-testid="traffic-sections">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-4">{tr.topSections}</p>
          <div className="space-y-3">
            {data.top_sections.map((s) => (
              <div key={s.section}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-white/70">{tr.sections[s.section] || s.section}</span>
                  <span className="text-white/40">{s.views}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF7900] rounded-full" style={{ width: `${(s.views / maxViews) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
